import {useEffect, useMemo, useState} from 'react';
import QRCode from 'qrcode';
import {
  ArrowLeftRight,
  Calculator,
  Check,
  Copy,
  Download,
  Gauge,
  KeyRound,
  Monitor,
  Percent,
  QrCode,
  RefreshCw,
  ShieldCheck,
  TextCursorInput,
  Tv,
  Zap,
  type LucideIcon,
} from 'lucide-react';

export const toolsHubSlug = '/ferramentas-gratuitas';

export type FreeToolDefinition = {
  id: string;
  slug: string;
  title: string;
  shortTitle: string;
  description: string;
  seoDescription: string;
  category: string;
  icon: LucideIcon;
  faq: Array<[string, string]>;
  tips: string[];
};

export const freeTools: FreeToolDefinition[] = [
  {
    id: 'porcentagem',
    slug: '/ferramentas/calculadora-de-porcentagem',
    title: 'Calculadora de porcentagem online',
    shortTitle: 'Calculadora de porcentagem',
    description: 'Calcule percentuais, aumentos e reduções com resultado imediato.',
    seoDescription: 'Calculadora de porcentagem grátis: descubra quanto é uma porcentagem de um valor, calcule aumentos, descontos e variação percentual online.',
    category: 'Cálculo',
    icon: Percent,
    faq: [
      ['Como calcular uma porcentagem de um valor?', 'Multiplique o valor pela porcentagem e divida por 100. Por exemplo, 15% de 200 é 200 × 15 ÷ 100, que resulta em 30.'],
      ['Como acrescentar ou retirar uma porcentagem?', 'Para acrescentar, multiplique o valor por 1 mais a taxa decimal. Para reduzir, multiplique por 1 menos a taxa decimal.'],
    ],
    tips: ['Use valores com vírgula ou ponto decimal.', 'O resultado mostra a parcela, o total com aumento e o total com redução.'],
  },
  {
    id: 'desconto',
    slug: '/ferramentas/calculadora-de-desconto',
    title: 'Calculadora de desconto',
    shortTitle: 'Calculadora de desconto',
    description: 'Veja preço final, economia real e desconto acumulado antes de comprar.',
    seoDescription: 'Calculadora de desconto online grátis: informe o preço e a porcentagem para descobrir o valor final e quanto você economiza.',
    category: 'Compras',
    icon: Calculator,
    faq: [
      ['Como calcular 20% de desconto?', 'Multiplique o preço por 0,20 para encontrar a economia e subtraia esse valor do preço original.'],
      ['Dois descontos de 10% equivalem a 20%?', 'Não. Descontos sucessivos são aplicados sobre valores diferentes. Dois descontos de 10% resultam em 19% de desconto total.'],
    ],
    tips: ['Compare o preço final, não apenas o percentual anunciado.', 'Frete e juros devem ser somados depois do desconto.'],
  },
  {
    id: 'qr-code',
    slug: '/ferramentas/gerador-de-qr-code',
    title: 'Gerador de QR Code grátis',
    shortTitle: 'Gerador de QR Code',
    description: 'Transforme link ou texto em QR Code, sem enviar o conteúdo para servidores.',
    seoDescription: 'Gerador de QR Code grátis e online: crie QR Code para link ou texto, baixe em PNG e processe tudo localmente no navegador.',
    category: 'Produtividade',
    icon: QrCode,
    faq: [
      ['O QR Code criado expira?', 'Não. Um QR Code estático guarda o texto ou link diretamente no desenho e não expira. O destino pode deixar de funcionar se o endereço mudar.'],
      ['O conteúdo do QR Code é enviado para algum servidor?', 'Não nesta ferramenta. A geração acontece localmente no navegador e o conteúdo digitado não é transmitido pelo GuiaSite.'],
    ],
    tips: ['Use links completos começando com https://.', 'Teste o arquivo com a câmera do celular antes de imprimir.'],
  },
  {
    id: 'senha',
    slug: '/ferramentas/gerador-de-senha-segura',
    title: 'Gerador de senha segura',
    shortTitle: 'Gerador de senha',
    description: 'Crie senhas aleatórias fortes usando o gerador criptográfico do navegador.',
    seoDescription: 'Gerador de senha forte e segura grátis: escolha tamanho, letras, números e símbolos. A senha é criada localmente no seu navegador.',
    category: 'Segurança',
    icon: KeyRound,
    faq: [
      ['Qual é um bom tamanho de senha?', 'Para contas comuns, 16 caracteres aleatórios já oferecem boa margem. Contas críticas podem usar 20 ou mais caracteres e autenticação em dois fatores.'],
      ['O GuiaSite guarda a senha gerada?', 'Não. A senha é criada no seu dispositivo e não é enviada nem armazenada pelo site.'],
    ],
    tips: ['Use uma senha diferente em cada serviço.', 'Guarde senhas em um gerenciador confiável e ative autenticação em dois fatores.'],
  },
  {
    id: 'contador-texto',
    slug: '/ferramentas/contador-de-palavras-e-caracteres',
    title: 'Contador de palavras e caracteres',
    shortTitle: 'Contador de palavras',
    description: 'Conte palavras, caracteres, parágrafos e tempo aproximado de leitura.',
    seoDescription: 'Contador de palavras e caracteres online grátis: veja palavras, caracteres com e sem espaços, parágrafos e tempo de leitura.',
    category: 'Texto',
    icon: TextCursorInput,
    faq: [
      ['Espaços contam como caracteres?', 'A ferramenta mostra os dois resultados: total de caracteres e caracteres sem espaços.'],
      ['Como o tempo de leitura é calculado?', 'A estimativa usa uma velocidade média de 200 palavras por minuto. O tempo real varia conforme dificuldade e familiaridade com o texto.'],
    ],
    tips: ['O texto permanece no navegador durante o uso.', 'A contagem é atualizada enquanto você digita.'],
  },
  {
    id: 'unidades',
    slug: '/ferramentas/conversor-de-unidades',
    title: 'Conversor de unidades online',
    shortTitle: 'Conversor de unidades',
    description: 'Converta comprimento, massa, temperatura e velocidade.',
    seoDescription: 'Conversor de unidades grátis: converta metros, quilômetros, polegadas, pés, quilos, libras, Celsius, Fahrenheit, km/h e mph.',
    category: 'Conversão',
    icon: ArrowLeftRight,
    faq: [
      ['Como converter Celsius para Fahrenheit?', 'Multiplique a temperatura em Celsius por 9/5 e some 32. A ferramenta aplica essa fórmula automaticamente.'],
      ['Quantos centímetros tem uma polegada?', 'Uma polegada equivale exatamente a 2,54 centímetros.'],
    ],
    tips: ['Escolha a categoria antes das unidades.', 'Temperatura usa fórmulas próprias; as demais categorias usam fatores de conversão.'],
  },
  {
    id: 'ppi',
    slug: '/ferramentas/calculadora-de-ppi',
    title: 'Calculadora de PPI de tela',
    shortTitle: 'Calculadora de PPI',
    description: 'Descubra densidade de pixels e tamanho de cada pixel em monitor, TV ou celular.',
    seoDescription: 'Calculadora de PPI grátis: informe resolução e tamanho da tela para calcular densidade de pixels, pixel pitch e nitidez.',
    category: 'Telas',
    icon: Monitor,
    faq: [
      ['O que é PPI?', 'PPI significa pixels por polegada. Quanto maior o número para o mesmo tipo de tela e distância, mais finos tendem a parecer textos e detalhes.'],
      ['Qual é o PPI de um monitor 27 polegadas QHD?', 'Uma tela de 27 polegadas com 2560 × 1440 pixels tem aproximadamente 109 PPI.'],
    ],
    tips: ['Use a diagonal nominal da tela em polegadas.', 'PPI não mede contraste, cor, brilho ou qualidade do painel.'],
  },
  {
    id: 'distancia-tv',
    slug: '/ferramentas/calculadora-distancia-tv',
    title: 'Calculadora de distância ideal para TV',
    shortTitle: 'Distância ideal para TV',
    description: 'Calcule uma faixa confortável conforme o tamanho da TV e o campo de visão.',
    seoDescription: 'Calculadora de distância ideal para TV: informe o tamanho em polegadas e descubra a faixa recomendada para assistir com conforto.',
    category: 'TV e sala',
    icon: Tv,
    faq: [
      ['Qual a distância ideal para uma TV de 55 polegadas?', 'Pelo campo de visão de 30 a 40 graus, uma TV 16:9 de 55 polegadas fica confortável aproximadamente entre 1,7 e 2,3 metros.'],
      ['TV 4K pode ser vista mais de perto?', 'Sim. A maior resolução reduz a percepção da grade de pixels em distâncias curtas, desde que o conteúdo também tenha boa qualidade.'],
    ],
    tips: ['A faixa usa campos de visão de aproximadamente 30° a 40°.', 'Conforto pessoal, móveis e qualidade do conteúdo também importam.'],
  },
  {
    id: 'energia',
    slug: '/ferramentas/calculadora-consumo-energia',
    title: 'Calculadora de consumo de energia',
    shortTitle: 'Consumo de energia',
    description: 'Estime kWh por mês e custo de uso de PC, TV, console ou eletroeletrônico.',
    seoDescription: 'Calculadora de consumo de energia: estime kWh e custo mensal de aparelhos usando potência, horas de uso e tarifa de eletricidade.',
    category: 'Economia',
    icon: Zap,
    faq: [
      ['Como calcular o consumo em kWh?', 'Multiplique a potência em watts pelas horas de uso e divida por 1.000. Para o mês, multiplique também pelo número de dias.'],
      ['A conta calculada será igual à fatura?', 'Não necessariamente. A tarifa final pode incluir impostos, bandeiras, taxas e outros aparelhos. O resultado é uma estimativa do equipamento informado.'],
    ],
    tips: ['Use a potência média real quando ela estiver disponível.', 'A potência máxima da fonte de um PC não é seu consumo constante.'],
  },
  {
    id: 'fps-hz',
    slug: '/ferramentas/calculadora-fps-hz-frame-time',
    title: 'Calculadora de FPS, Hz e frame time',
    shortTitle: 'FPS, Hz e frame time',
    description: 'Converta FPS em milissegundos por quadro e compare com a frequência do monitor.',
    seoDescription: 'Calculadora de FPS, Hz e frame time: converta quadros por segundo em milissegundos e compare FPS do jogo com Hz do monitor.',
    category: 'Games',
    icon: Gauge,
    faq: [
      ['Quantos milissegundos tem um quadro a 60 FPS?', 'A 60 FPS, cada quadro dura aproximadamente 16,67 ms. A 120 FPS são 8,33 ms; a 144 FPS, 6,94 ms.'],
      ['Um monitor de 144 Hz mostra 300 FPS?', 'Ele pode receber o jogo rodando a 300 FPS, mas atualiza fisicamente até 144 vezes por segundo. FPS extra ainda pode reduzir a idade do quadro e a latência, mas não vira 300 atualizações visíveis.'],
    ],
    tips: ['Frame time é 1.000 dividido pelos FPS.', 'Estabilidade de quadros pode importar mais que uma média alta com oscilações.'],
  },
];

export function getFreeTool(path: string) {
  return freeTools.find((tool) => tool.slug === path);
}

const brNumber = new Intl.NumberFormat('pt-BR', {maximumFractionDigits: 2});
const brPrecise = new Intl.NumberFormat('pt-BR', {maximumFractionDigits: 8});
const brMoney = new Intl.NumberFormat('pt-BR', {style: 'currency', currency: 'BRL'});

function parseNumber(value: string) {
  const normalized = value.trim().replace(/\s/g, '').replace(',', '.');
  const parsed = Number(normalized);
  return Number.isFinite(parsed) ? parsed : 0;
}

function ToolField({label, value, onChange, suffix, min, max, step = 'any'}: {
  label: string;
  value: string;
  onChange: (value: string) => void;
  suffix?: string;
  min?: number;
  max?: number;
  step?: string;
}) {
  return (
    <label className="tool-field">
      <span>{label}</span>
      <span className="tool-input-wrap">
        <input type="number" inputMode="decimal" value={value} min={min} max={max} step={step} onChange={(event) => onChange(event.target.value)} />
        {suffix && <em>{suffix}</em>}
      </span>
    </label>
  );
}

function ResultCard({label, value, note}: {label: string; value: string; note?: string}) {
  return (
    <div className="tool-result-card" aria-live="polite">
      <span>{label}</span>
      <strong>{value}</strong>
      {note && <small>{note}</small>}
    </div>
  );
}

function PercentageTool() {
  const [value, setValue] = useState('1000');
  const [rate, setRate] = useState('15');
  const amount = parseNumber(value);
  const percent = parseNumber(rate);
  const portion = amount * percent / 100;

  return <div className="tool-workspace">
    <div className="tool-fields two-columns">
      <ToolField label="Valor" value={value} onChange={setValue} />
      <ToolField label="Porcentagem" value={rate} onChange={setRate} suffix="%" />
    </div>
    <div className="tool-results three-results">
      <ResultCard label={`${brNumber.format(percent)}% de ${brNumber.format(amount)}`} value={brNumber.format(portion)} />
      <ResultCard label="Com acréscimo" value={brNumber.format(amount + portion)} note={`+ ${brNumber.format(portion)}`} />
      <ResultCard label="Com redução" value={brNumber.format(amount - portion)} note={`- ${brNumber.format(portion)}`} />
    </div>
  </div>;
}

function DiscountTool() {
  const [price, setPrice] = useState('2499');
  const [discount, setDiscount] = useState('12');
  const original = parseNumber(price);
  const rate = Math.min(100, Math.max(0, parseNumber(discount)));
  const savings = original * rate / 100;
  return <div className="tool-workspace">
    <div className="tool-fields two-columns">
      <ToolField label="Preço original" value={price} onChange={setPrice} suffix="R$" min={0} />
      <ToolField label="Desconto" value={discount} onChange={setDiscount} suffix="%" min={0} max={100} />
    </div>
    <div className="tool-results">
      <ResultCard label="Preço final" value={brMoney.format(original - savings)} note={`Economia de ${brMoney.format(savings)}`} />
    </div>
  </div>;
}

function QrCodeTool() {
  const [text, setText] = useState('https://www.guiasite.com');
  const [dataUrl, setDataUrl] = useState('');
  const [error, setError] = useState('');
  useEffect(() => {
    let cancelled = false;
    if (!text.trim()) {
      setDataUrl('');
      return () => { cancelled = true; };
    }

    void import('qrcode')
      .then(({default: QRCode}) => QRCode.toDataURL(text, {
        width: 720,
        margin: 2,
        errorCorrectionLevel: 'M',
        color: {dark: '#061f33', light: '#ffffff'},
      }))
      .then((url) => {
        if (!cancelled) {
          setDataUrl(url);
          setError('');
        }
      })
      .catch(() => {
        if (!cancelled) setError('Não foi possível gerar o QR Code.');
      });

    return () => { cancelled = true; };
  }, [text]);

  return <div className="tool-workspace qr-workspace">
    <label className="tool-field tool-field-wide">
      <span>Link ou texto</span>
      <textarea value={text} onChange={(event) => setText(event.target.value)} rows={4} maxLength={2000} />
    </label>
    <div className="qr-preview">
      {dataUrl ? <img src={dataUrl} alt="QR Code gerado a partir do conteúdo informado" /> : <span>{error || 'Digite um conteúdo para gerar.'}</span>}
    </div>
    {dataUrl && <a className="tool-primary-action" href={dataUrl} download="qrcode-guiasite.png"><Download size={18} /> Baixar PNG</a>}
    <p className="tool-privacy-note"><ShieldCheck size={17} /> Processamento local: o conteúdo não é enviado ao GuiaSite.</p>
  </div>;
}

function secureIndex(max: number) {
  if (max <= 1) return 0;
  const array = new Uint32Array(1);
  const limit = Math.floor(0x100000000 / max) * max;
  do window.crypto.getRandomValues(array); while (array[0] >= limit);
  return array[0] % max;
}

function createSecurePassword(length: number, sets: string[]) {
  if (!sets.length) return '';
  const chars = sets.join('');
  const output = sets.map((set) => set[secureIndex(set.length)]);
  while (output.length < length) output.push(chars[secureIndex(chars.length)]);
  for (let index = output.length - 1; index > 0; index -= 1) {
    const swap = secureIndex(index + 1);
    [output[index], output[swap]] = [output[swap], output[index]];
  }
  return output.join('');
}

function PasswordTool() {
  const [length, setLength] = useState(18);
  const [upper, setUpper] = useState(true);
  const [lower, setLower] = useState(true);
  const [numbers, setNumbers] = useState(true);
  const [symbols, setSymbols] = useState(true);
  const [password, setPassword] = useState('');
  const [copied, setCopied] = useState(false);
  const sets = useMemo(() => [upper && 'ABCDEFGHJKLMNPQRSTUVWXYZ', lower && 'abcdefghijkmnopqrstuvwxyz', numbers && '23456789', symbols && '!@#$%&*+-_=.?'].filter(Boolean) as string[], [upper, lower, numbers, symbols]);
  const regenerate = () => { setPassword(createSecurePassword(Math.max(length, sets.length), sets)); setCopied(false); };
  useEffect(regenerate, [length, upper, lower, numbers, symbols]);
  const copy = async () => { if (!password) return; await navigator.clipboard.writeText(password); setCopied(true); };

  return <div className="tool-workspace">
    <label className="tool-range-field"><span>Tamanho da senha</span><strong>{length} caracteres</strong><input type="range" min="8" max="64" value={length} onChange={(event) => setLength(Number(event.target.value))} /></label>
    <div className="tool-option-grid">
      <label><input type="checkbox" checked={upper} onChange={(event) => setUpper(event.target.checked)} /> Maiúsculas</label>
      <label><input type="checkbox" checked={lower} onChange={(event) => setLower(event.target.checked)} /> Minúsculas</label>
      <label><input type="checkbox" checked={numbers} onChange={(event) => setNumbers(event.target.checked)} /> Números</label>
      <label><input type="checkbox" checked={symbols} onChange={(event) => setSymbols(event.target.checked)} /> Símbolos</label>
    </div>
    <div className="password-output"><code>{password || 'Selecione ao menos um conjunto'}</code><button type="button" onClick={copy} disabled={!password} title="Copiar senha">{copied ? <Check size={19} /> : <Copy size={19} />}<span>{copied ? 'Copiada' : 'Copiar'}</span></button></div>
    <button className="tool-secondary-action" type="button" onClick={regenerate} disabled={!sets.length}><RefreshCw size={18} /> Gerar outra senha</button>
    <p className="tool-privacy-note"><ShieldCheck size={17} /> A senha é criada pelo gerador criptográfico do seu navegador.</p>
  </div>;
}

function TextCounterTool() {
  const [text, setText] = useState('');
  const words = text.trim() ? text.trim().split(/\s+/u).length : 0;
  const characters = [...text].length;
  const noSpaces = [...text.replace(/\s/gu, '')].length;
  const paragraphs = text.trim() ? text.trim().split(/\n\s*\n/u).filter(Boolean).length : 0;
  const readMinutes = words ? Math.max(1, Math.ceil(words / 200)) : 0;
  return <div className="tool-workspace">
    <label className="tool-field tool-field-wide"><span>Seu texto</span><textarea value={text} onChange={(event) => setText(event.target.value)} rows={10} placeholder="Digite ou cole o texto aqui..." /></label>
    <div className="tool-results metric-grid">
      <ResultCard label="Palavras" value={brNumber.format(words)} />
      <ResultCard label="Caracteres" value={brNumber.format(characters)} />
      <ResultCard label="Sem espaços" value={brNumber.format(noSpaces)} />
      <ResultCard label="Parágrafos" value={brNumber.format(paragraphs)} />
      <ResultCard label="Leitura estimada" value={readMinutes ? `${readMinutes} min` : '0 min'} />
    </div>
  </div>;
}

type Unit = {label: string; factor?: number; toBase?: (value: number) => number; fromBase?: (value: number) => number};
const unitGroups: Record<string, {label: string; units: Record<string, Unit>}> = {
  length: {label: 'Comprimento', units: {m:{label:'Metro',factor:1},km:{label:'Quilômetro',factor:1000},cm:{label:'Centímetro',factor:.01},mm:{label:'Milímetro',factor:.001},in:{label:'Polegada',factor:.0254},ft:{label:'Pé',factor:.3048}}},
  mass: {label: 'Massa', units: {kg:{label:'Quilograma',factor:1},g:{label:'Grama',factor:.001},lb:{label:'Libra',factor:.45359237},oz:{label:'Onça',factor:.028349523125}}},
  temperature: {label: 'Temperatura', units: {c:{label:'Celsius',toBase:v=>v,fromBase:v=>v},f:{label:'Fahrenheit',toBase:v=>(v-32)*5/9,fromBase:v=>v*9/5+32},k:{label:'Kelvin',toBase:v=>v-273.15,fromBase:v=>v+273.15}}},
  speed: {label: 'Velocidade', units: {kmh:{label:'km/h',factor:1},ms:{label:'m/s',factor:3.6},mph:{label:'mph',factor:1.609344},knot:{label:'Nó',factor:1.852}}},
};

function UnitConverterTool() {
  const [group, setGroup] = useState('length');
  const [value, setValue] = useState('1');
  const keys = Object.keys(unitGroups[group].units);
  const [from, setFrom] = useState('m');
  const [to, setTo] = useState('km');
  const changeGroup = (nextGroup: string) => {
    const nextUnits = Object.keys(unitGroups[nextGroup].units);
    setGroup(nextGroup);
    setFrom(nextUnits[0]);
    setTo(nextUnits[1] ?? nextUnits[0]);
  };
  const safeFrom = unitGroups[group].units[from] ? from : keys[0];
  const safeTo = unitGroups[group].units[to] ? to : (keys[1] ?? keys[0]);
  const source = unitGroups[group].units[safeFrom];
  const target = unitGroups[group].units[safeTo];
  const amount = parseNumber(value);
  const base = source.toBase ? source.toBase(amount) : amount * (source.factor ?? 1);
  const result = target.fromBase ? target.fromBase(base) : base / (target.factor ?? 1);
  return <div className="tool-workspace">
    <label className="tool-field"><span>Categoria</span><select value={group} onChange={(event) => changeGroup(event.target.value)}>{Object.entries(unitGroups).map(([key,item])=><option key={key} value={key}>{item.label}</option>)}</select></label>
    <div className="tool-fields converter-grid">
      <ToolField label="Valor" value={value} onChange={setValue} />
      <label className="tool-field"><span>De</span><select value={safeFrom} onChange={(event)=>setFrom(event.target.value)}>{keys.map(key=><option value={key} key={key}>{unitGroups[group].units[key].label}</option>)}</select></label>
      <label className="tool-field"><span>Para</span><select value={safeTo} onChange={(event)=>setTo(event.target.value)}>{keys.map(key=><option value={key} key={key}>{unitGroups[group].units[key].label}</option>)}</select></label>
    </div>
    <ResultCard label={`${source.label} para ${target.label}`} value={brPrecise.format(result)} />
  </div>;
}

function PpiTool() {
  const [width, setWidth] = useState('2560');
  const [height, setHeight] = useState('1440');
  const [diagonal, setDiagonal] = useState('27');
  const w=parseNumber(width), h=parseNumber(height), d=Math.max(.1,parseNumber(diagonal));
  const ppi=Math.sqrt(w*w+h*h)/d;
  const pitch=25.4/ppi;
  return <div className="tool-workspace">
    <div className="tool-fields three-columns">
      <ToolField label="Largura" value={width} onChange={setWidth} suffix="px" min={1} />
      <ToolField label="Altura" value={height} onChange={setHeight} suffix="px" min={1} />
      <ToolField label="Diagonal" value={diagonal} onChange={setDiagonal} suffix="pol" min={1} />
    </div>
    <div className="tool-results"><ResultCard label="Densidade" value={`${brNumber.format(ppi)} PPI`} note={`Pixel pitch: ${brNumber.format(pitch)} mm`} /></div>
  </div>;
}

function TvDistanceTool() {
  const [size, setSize] = useState('55');
  const [resolution, setResolution] = useState('4K');
  const diagonalIn=Math.max(1,parseNumber(size));
  const widthMeters=diagonalIn*0.0254*(16/Math.sqrt(337));
  const near=widthMeters/(2*Math.tan(20*Math.PI/180));
  const far=widthMeters/(2*Math.tan(15*Math.PI/180));
  return <div className="tool-workspace">
    <div className="tool-fields two-columns">
      <ToolField label="Tamanho da TV" value={size} onChange={setSize} suffix="pol" min={1} />
      <label className="tool-field"><span>Resolução</span><select value={resolution} onChange={(event)=>setResolution(event.target.value)}><option>4K</option><option>Full HD</option><option>8K</option></select></label>
    </div>
    <div className="tool-results"><ResultCard label="Faixa pelo campo de visão" value={`${brNumber.format(near)} m a ${brNumber.format(far)} m`} note={`${resolution}: use a faixa como ponto de partida e ajuste pelo conforto.`} /></div>
  </div>;
}

function EnergyTool() {
  const [watts,setWatts]=useState('350');
  const [hours,setHours]=useState('4');
  const [days,setDays]=useState('30');
  const [tariff,setTariff]=useState('1.00');
  const kwh=parseNumber(watts)/1000*parseNumber(hours)*parseNumber(days);
  const cost=kwh*parseNumber(tariff);
  return <div className="tool-workspace">
    <div className="tool-fields two-columns">
      <ToolField label="Potência média" value={watts} onChange={setWatts} suffix="W" min={0} />
      <ToolField label="Uso por dia" value={hours} onChange={setHours} suffix="h" min={0} max={24} />
      <ToolField label="Dias no mês" value={days} onChange={setDays} min={0} max={31} />
      <ToolField label="Tarifa por kWh" value={tariff} onChange={setTariff} suffix="R$" min={0} />
    </div>
    <div className="tool-results two-results"><ResultCard label="Consumo mensal" value={`${brNumber.format(kwh)} kWh`} /><ResultCard label="Custo estimado" value={brMoney.format(cost)} note="Sem impostos ou cobranças adicionais específicas da fatura." /></div>
  </div>;
}

function FpsTool() {
  const [fps,setFps]=useState('144');
  const [hz,setHz]=useState('180');
  const fpsValue=Math.max(.01,parseNumber(fps));
  const hzValue=Math.max(.01,parseNumber(hz));
  const visible=Math.min(fpsValue,hzValue);
  return <div className="tool-workspace">
    <div className="tool-fields two-columns"><ToolField label="FPS do jogo" value={fps} onChange={setFps} suffix="FPS" min={1} /><ToolField label="Frequência do monitor" value={hz} onChange={setHz} suffix="Hz" min={1} /></div>
    <div className="tool-results three-results">
      <ResultCard label="Tempo por quadro" value={`${brNumber.format(1000/fpsValue)} ms`} />
      <ResultCard label="Ciclo da tela" value={`${brNumber.format(1000/hzValue)} ms`} />
      <ResultCard label="Atualizações aproveitáveis" value={`${brNumber.format(visible)} por segundo`} note={fpsValue>=hzValue?'O jogo alimenta toda a frequência da tela.':'Os FPS limitam novos quadros exibidos.'} />
    </div>
  </div>;
}

function ToolRunner({id}: {id: string}) {
  switch(id) {
    case 'porcentagem': return <PercentageTool />;
    case 'desconto': return <DiscountTool />;
    case 'qr-code': return <QrCodeTool />;
    case 'senha': return <PasswordTool />;
    case 'contador-texto': return <TextCounterTool />;
    case 'unidades': return <UnitConverterTool />;
    case 'ppi': return <PpiTool />;
    case 'distancia-tv': return <TvDistanceTool />;
    case 'energia': return <EnergyTool />;
    case 'fps-hz': return <FpsTool />;
    default: return null;
  }
}

export function ToolsHubContent() {
  return <>
    <section className="tools-hero">
      <div className="tools-hero-copy">
        <a className="breadcrumb" href="/">GuiaSite / Ferramentas gratuitas</a>
        <p className="section-kicker">Úteis, rápidas e privadas</p>
        <h1>Ferramentas gratuitas para resolver agora.</h1>
        <p>Calculadoras, conversores e geradores que funcionam direto no navegador. Sem cadastro, sem anúncios bloqueando o resultado e sem transformar uma tarefa simples em um formulário interminável.</p>
        <div className="tools-trust-row"><span><Check size={17}/> Resultado imediato</span><span><ShieldCheck size={17}/> Processamento local</span><span><Zap size={17}/> Funciona no celular</span></div>
      </div>
      <div className="tools-hero-console" aria-hidden="true">
        <span>GUIASITE / UTILITÁRIOS</span>
        <strong>10</strong>
        <em>ferramentas disponíveis</em>
        <div><i/><i/><i/><i/><i/></div>
      </div>
    </section>
    <section className="tools-directory" aria-labelledby="tools-directory-title">
      <div className="section-heading"><div><p className="section-kicker">Escolha uma ferramenta</p><h2 id="tools-directory-title">Uma resposta clara em poucos segundos.</h2></div></div>
      <div className="tools-grid">
        {freeTools.map((tool,index)=>{const Icon=tool.icon; return <a className="tool-directory-card" href={tool.slug} key={tool.id}>
          <span className="tool-card-index">{String(index+1).padStart(2,'0')}</span><Icon size={25} aria-hidden="true"/><small>{tool.category}</small><h2>{tool.shortTitle}</h2><p>{tool.description}</p><strong>Abrir ferramenta <ArrowLeftRight size={16}/></strong>
        </a>})}
      </div>
    </section>
    <section className="tools-method-band">
      <div><ShieldCheck size={25}/><strong>Privacidade por padrão</strong><p>Os cálculos acontecem no seu dispositivo. A ferramenta de senha e o QR Code não enviam o conteúdo digitado ao GuiaSite.</p></div>
      <div><Calculator size={25}/><strong>Fórmulas transparentes</strong><p>Resultados vêm acompanhados de contexto, unidade e limite de uso para evitar precisão falsa.</p></div>
      <div><Monitor size={25}/><strong>Feitas para qualquer tela</strong><p>Controles grandes, resultados legíveis e funcionamento completo em celular ou computador.</p></div>
    </section>
  </>;
}

export function FreeToolContent({path}: {path: string}) {
  const tool=getFreeTool(path) ?? freeTools[0];
  const Icon=tool.icon;
  return <>
    <section className="tool-page-hero">
      <div>
        <a className="breadcrumb" href={toolsHubSlug}>GuiaSite / Ferramentas / {tool.category}</a>
        <p className="section-kicker">Ferramenta gratuita</p>
        <h1>{tool.title}</h1>
        <p>{tool.description} Tudo roda no seu navegador e o resultado aparece imediatamente.</p>
      </div>
      <span className="tool-page-icon"><Icon size={48}/></span>
    </section>
    <section className="tool-page-layout">
      <div className="tool-panel"><ToolRunner id={tool.id}/></div>
      <aside className="tool-context-card"><span>Antes de usar</span><h2>Resultado útil, sem falsa precisão.</h2><ul>{tool.tips.map((tip)=><li key={tip}><Check size={16}/>{tip}</li>)}</ul></aside>
    </section>
    <section className="tool-explanation">
      <div><p className="section-kicker">Como o resultado é calculado</p><h2>{tool.shortTitle}: rápido, mas com contexto.</h2><p>{tool.faq[0][1]}</p><p>Os valores servem como referência prática. Quando a decisão envolver segurança, instalação elétrica, saúde ou compra de alto valor, confirme as condições reais e as especificações do produto.</p></div>
      <div className="tool-faq-list"><h2>Dúvidas frequentes</h2>{tool.faq.map(([question,answer])=><details key={question}><summary>{question}</summary><p>{answer}</p></details>)}</div>
    </section>
    <section className="more-tools-section"><div className="section-heading"><div><p className="section-kicker">Continue resolvendo</p><h2>Outras ferramentas úteis</h2></div><a href={toolsHubSlug}>Ver todas</a></div><div className="more-tools-grid">{freeTools.filter(item=>item.id!==tool.id).slice(0,3).map(item=>{const MoreIcon=item.icon;return <a href={item.slug} key={item.id}><MoreIcon size={21}/><span><strong>{item.shortTitle}</strong><small>{item.category}</small></span></a>})}</div></section>
  </>;
}


